/**
 * Agent Card Component
 * UF-008: Agent Marketplace UI
 */

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import type { MarketplaceAgent } from "./types";
import { CATEGORY_CONFIG, PRICING_CONFIG } from "./types";

// =============================================================================
// Types
// =============================================================================

interface AgentCardProps {
  agent: MarketplaceAgent;
  onClick: () => void;
  index?: number;
}

// =============================================================================
// Animation Variants
// =============================================================================

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      delay: index * 0.08,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

// =============================================================================
// Icon Components
// =============================================================================

function AgentIcon({ icon, color }: { icon?: string; color?: string }) {
  const iconColor = color || "#758eff";

  // Simple icon mapping
  const icons: Record<string, JSX.Element> = {
    brain: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24A2.5 2.5 0 0 1 9.5 2" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24A2.5 2.5 0 0 0 14.5 2" />
      </svg>
    ),
    "chart-line": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),
    "git-branch": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="6" x2="6" y1="3" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </svg>
    ),
    "message-circle": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      </svg>
    ),
    "eye-off": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
        <path d="m2 2 20 20" />
      </svg>
    ),
    code: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    settings: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  };

  return (
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{
        background: `linear-gradient(135deg, ${iconColor}20, ${iconColor}10)`,
        border: `1px solid ${iconColor}30`,
      }}
    >
      <div className="w-6 h-6" style={{ color: iconColor }}>
        {icons[icon || "brain"] || icons.brain}
      </div>
    </div>
  );
}

// =============================================================================
// Rating Stars Component
// =============================================================================

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-3.5 h-3.5 ${star <= rating ? "text-hvme-gold" : "text-hvme-text-muted/30"}`}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

// =============================================================================
// Price Badge Component
// =============================================================================

function PriceBadge({ agent }: { agent: MarketplaceAgent }) {
  const { pricing } = agent;
  const config = PRICING_CONFIG[pricing.model];

  let priceText = config.label;
  if (pricing.model !== "free" && pricing.price) {
    priceText = `$${pricing.price}${pricing.billingPeriod === "monthly" ? "/mo" : pricing.billingPeriod === "yearly" ? "/yr" : ""}`;
  }

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${config.badge}`}>
      {priceText}
    </span>
  );
}

// =============================================================================
// Download Count Formatter
// =============================================================================

function formatDownloads(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

// =============================================================================
// Main Component
// =============================================================================

export function AgentCard({ agent, onClick, index = 0 }: AgentCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const categoryConfig = CATEGORY_CONFIG[agent.category];

  return (
    <motion.div
      ref={cardRef}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      whileHover={{ y: -4 }}
      className="
        relative overflow-hidden rounded-2xl cursor-pointer
        bg-hvme-bg-surface border border-hvme-border
        hover:border-hvme-border-hover
        transition-all duration-300
      "
    >
      {/* Hover Glow Effect */}
      <motion.div
        className="absolute pointer-events-none z-0"
        style={{
          width: 250,
          height: 250,
          left: mousePosition.x - 125,
          top: mousePosition.y - 125,
          background: `radial-gradient(circle, ${categoryConfig.color}15 0%, transparent 70%)`,
          borderRadius: "50%",
        }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Owned/Deployed Badge */}
      {(agent.owned || agent.deployed) && (
        <div className="absolute top-3 right-3 z-20">
          {agent.deployed ? (
            <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-hvme-green/20 text-hvme-green border border-hvme-green/30">
              Deployed
            </span>
          ) : agent.owned ? (
            <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-hvme-primary/20 text-hvme-primary border border-hvme-primary/30">
              Owned
            </span>
          ) : null}
        </div>
      )}

      {/* Card Content */}
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <AgentIcon icon={agent.icon} color={agent.iconColor} />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-hvme-text-primary truncate">
              {agent.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: categoryConfig.color }}
              />
              <span className="text-xs text-hvme-text-muted">
                {categoryConfig.label}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-hvme-text-secondary leading-relaxed line-clamp-2 mb-4">
          {agent.shortDescription}
        </p>

        {/* Stats Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <RatingStars rating={Math.round(agent.rating)} />
            <span className="text-sm text-hvme-text-secondary">{agent.rating.toFixed(1)}</span>
            <span className="text-xs text-hvme-text-muted">({agent.reviewCount})</span>
          </div>
          <div className="flex items-center gap-1 text-hvme-text-muted">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" x2="12" y1="15" y2="3" />
            </svg>
            <span className="text-xs">{formatDownloads(agent.downloads)}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-hvme-border/50">
          <PriceBadge agent={agent} />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="
              px-4 py-2 rounded-lg text-sm font-medium
              bg-hvme-primary/10 text-hvme-primary border border-hvme-primary/20
              hover:bg-hvme-primary/20 hover:border-hvme-primary/40
              transition-all duration-200
            "
          >
            View Details
          </motion.button>
        </div>
      </div>

      {/* Bottom Accent Line on Hover */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[2px] pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${categoryConfig.color}80, transparent)`,
        }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{
          scaleX: isHovered ? 1 : 0,
          opacity: isHovered ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}

export default AgentCard;
